import { LocalDateSchema, RitualKindSchema } from '@projoint/shared';
import { z } from 'zod';

import { parseTaskKey } from '@/shared/lib/use-task-url';

/**
 * `/my/work` search params (§4 W3-B, §5 W4-G):
 * `?view=today|week|unplanned|later|all&weekOf=&role=&as=&status=&priority=&due=&project=&team=&sort=&task=`.
 *
 * Every field is optional and tolerant — a bad value is dropped instead of
 * failing the route (old bookmarks, hand-edited links). Multi-value fields
 * (status, priority) are comma-joined. Filters apply to view=all; `weekOf`
 * (any day of the week shown) to view=week; `task=KEY` is the open task
 * (`useTaskUrl`, so Back closes it).
 *
 * `role=waiting` is All › "Waiting on others" (the Delegated view, §2.3).
 *
 * `ritual=morning|wrap_up|weekly_review` (W6) is where a ritual push lands
 * (`RITUAL_PUSH_URLS`): morning → the ✨ planner when AI is ready, wrap_up →
 * the Wrap-up flow, weekly_review → the 4-step review. Closing the flow drops it.
 *
 * `/my/tasks` now redirects to the Home "My tasks" tab (`/?tab=tasks`,
 * founder 2026-10-04); `legacyTasksSearch` is kept for old filter links.
 */

export const MY_WORK_VIEWS = ['today', 'week', 'unplanned', 'later', 'all'] as const;
export type MyWorkPageView = (typeof MY_WORK_VIEWS)[number];

/**
 * The tabs (founder 2026-10-19): All (the default — `/my/work` with no
 * `view`) · Today · Week. `unplanned` and `later` stay valid views — they
 * are All with Filters › Plan set, so old links, onboarding and the capture
 * defaults keep working.
 */
export type MyWorkTab = 'today' | 'all' | 'week';
/** The view `/my/work` opens with no `?view=`. */
export const DEFAULT_VIEW: MyWorkPageView = 'all';

export function tabOf(view: MyWorkPageView): MyWorkTab {
  return view === 'unplanned' || view === 'later' ? 'all' : view;
}

/** All › role chips: the server roles plus "Waiting on others" (view=delegated). */
export const ALL_ROLES = ['mine', 'created', 'collaborating', 'waiting'] as const;

const DUES = ['all', 'today', 'tomorrow', 'this-week', 'overdue'] as const;
/** Mine › As (`?as=`): only tasks where I'm the assignee / the respondent. */
const AS = ['assignee', 'respondent'] as const;
const SORTS = ['updated', 'created', 'priority', 'due'] as const;

const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values).optional().catch(undefined);

// The router's default parser turns `?priority=1` into the number 1.
const csv = (allowed: (v: string) => boolean) =>
  z
    .unknown()
    .optional()
    .transform((v): string | undefined => {
      if (typeof v !== 'string' && typeof v !== 'number') return undefined;
      const kept = String(v)
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '' && allowed(s));
      return kept.length > 0 ? kept.join(',') : undefined;
    });

const STATUS_CATEGORIES = new Set(['open', 'in_progress', 'review', 'done', 'cancelled']);
const PRIORITIES = new Set(['1', '2', '3', '4']);

export const myWorkSearchSchema = z.object({
  view: optionalEnum(MY_WORK_VIEWS),
  weekOf: LocalDateSchema.optional().catch(undefined),
  role: optionalEnum(ALL_ROLES),
  as: optionalEnum(AS),
  status: csv((s) => STATUS_CATEGORIES.has(s)),
  priority: csv((s) => PRIORITIES.has(s)),
  due: optionalEnum(DUES),
  project: z.string().uuid().optional().catch(undefined),
  team: z.string().uuid().optional().catch(undefined),
  sort: optionalEnum(SORTS),
  /** A ritual flow to open (W6 push targets). */
  ritual: RitualKindSchema.optional().catch(undefined),
  /** The open task (`useTaskUrl`'s `?task=KEY`); kept so a reload reopens it. */
  task: z
    .unknown()
    .optional()
    .transform((v) => parseTaskKey(v) ?? undefined),
});

export type MyWorkSearch = z.infer<typeof myWorkSearchSchema>;

/** Parse raw search params (never throws). */
export function parseMyWorkSearch(raw: Record<string, unknown>): MyWorkSearch {
  const parsed = myWorkSearchSchema.safeParse(raw ?? {});
  return parsed.success ? stripUndefined(parsed.data) : {};
}

function stripUndefined<T extends Record<string, unknown>>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

/**
 * The search for another view: drops the open task, a ritual flow and the
 * params of the view being left (`weekOf` belongs to Week; the filters stay,
 * they belong to All and survive a round trip through Today).
 */
export function searchForView(search: MyWorkSearch, view: MyWorkPageView): MyWorkSearch {
  const { view: _view, task: _task, weekOf: _weekOf, ritual: _ritual, ...rest } = search;
  return view === DEFAULT_VIEW ? rest : { ...rest, view };
}

/**
 * Old `/my/tasks?role=…&status=…` → `/my/work` search with `view=all`.
 * Roles: "Created by me" (`author`) → `created`; Assigned to me
 * (`assignee`) → Mine · `as=assignee`; Respondent → Mine · `as=respondent`;
 * Any role → the default `mine`. Everything else carries over as is.
 */
export function legacyTasksSearch(raw: Record<string, unknown>): MyWorkSearch {
  const role = raw.role === 'author' ? 'created' : undefined;
  const as = raw.role === 'assignee' || raw.role === 'respondent' ? raw.role : undefined;
  const { role: _ignored, ...rest } = raw;
  return {
    ...parseMyWorkSearch(rest),
    view: 'all',
    ...(role ? { role } : {}),
    ...(as ? { as } : {}),
  };
}
